import { z } from 'zod';
import { esRutValido, normalizarRut } from '../utiles/formato.js';
/**
 * Piezas reutilizables de validación.
 *
 * Los mismos esquemas se usan en el `ValidationPipe` de NestJS y en los
 * formularios de React vía `react-hook-form`. Una sola definición significa que
 * el mensaje de error que ve el usuario en el formulario es exactamente el que
 * aplicaría el servidor: no hay forma de que las dos validaciones se
 * contradigan.
 */
/** Identificador de entidad (cuid2 generado por Prisma). */
export const idSchema = z.string().min(1, 'Identificador requerido');
/** Texto obligatorio, recortado, con largo máximo. */
export const textoRequerido = (max = 255, campo = 'Este campo') => z
    .string()
    .trim()
    .min(1, `${campo} es obligatorio`)
    .max(max, `${campo} no puede superar ${max} caracteres`);
/** Texto opcional: convierte cadena vacía en `undefined` para no guardar basura. */
export const textoOpcional = (max = 255) => z
    .string()
    .trim()
    .max(max, `No puede superar ${max} caracteres`)
    .optional()
    .transform((valor) => (valor === '' ? undefined : valor));
/**
 * Monto en pesos chilenos: entero, no negativo.
 * El redondeo se aplica aquí para que a la base nunca llegue un decimal.
 */
export const montoSchema = z
    .number({ invalid_type_error: 'Debe ser un número' })
    .finite('Monto inválido')
    .min(0, 'El monto no puede ser negativo')
    .max(999_999_999_999, 'Monto fuera de rango')
    .transform((valor) => Math.round(valor));
export const montoOpcional = montoSchema.optional().nullable();
/** Cantidad de días: entero no negativo. */
export const diasSchema = z
    .number({ invalid_type_error: 'Debe ser un número de días' })
    .int('Debe ser un número entero de días')
    .min(0, 'No puede ser negativo')
    .max(3650, 'No puede superar 10 años');
/** Porcentaje 0-100. */
export const porcentajeSchema = z
    .number({ invalid_type_error: 'Debe ser un porcentaje' })
    .min(0, 'No puede ser negativo')
    .max(100, 'No puede superar 100%');
/**
 * Fecha en formato `YYYY-MM-DD`, o un `Date`.
 * Se normaliza siempre a la cadena ISO de solo fecha.
 */
export const fechaSchema = z
    .union([z.string(), z.date()])
    .transform((valor, ctx) => {
    if (valor instanceof Date) {
        if (Number.isNaN(valor.getTime())) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Fecha inválida' });
            return z.NEVER;
        }
        return valor.toISOString().slice(0, 10);
    }
    const texto = valor.trim();
    if (!/^\d{4}-\d{2}-\d{2}/.test(texto)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'La fecha debe tener formato AAAA-MM-DD' });
        return z.NEVER;
    }
    const soloFecha = texto.slice(0, 10);
    if (Number.isNaN(new Date(`${soloFecha}T12:00:00Z`).getTime())) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Fecha inválida' });
        return z.NEVER;
    }
    return soloFecha;
});
export const fechaOpcional = z
    .union([z.string(), z.date(), z.null()])
    .optional()
    .transform((valor) => {
    if (valor == null || valor === '')
        return undefined;
    const resultado = fechaSchema.safeParse(valor);
    return resultado.success ? resultado.data : undefined;
});
/** RUT chileno validado contra su dígito verificador y normalizado. */
export const rutSchema = z
    .string()
    .trim()
    .refine((valor) => valor === '' || esRutValido(valor), {
    message: 'RUT inválido: revisa el dígito verificador',
})
    .transform((valor) => (valor === '' ? undefined : normalizarRut(valor)))
    .optional();
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .email('Correo electrónico inválido')
    .max(255);
/**
 * Contraseña. El mínimo de 10 caracteres, en vez de los 8 habituales, es una
 * decisión consciente: este sistema va a quedar expuesto en un VPS con datos
 * comerciales y financieros de la empresa.
 */
export const passwordSchema = z
    .string()
    .min(10, 'La contraseña debe tener al menos 10 caracteres')
    .max(128, 'La contraseña es demasiado larga')
    .refine((val) => /[a-z]/.test(val), {
    message: 'La contraseña debe contener al menos una letra minúscula',
})
    .refine((val) => /[A-Z]/.test(val), {
    message: 'La contraseña debe contener al menos una letra mayúscula',
})
    .refine((val) => /[\d\W]/.test(val), {
    message: 'La contraseña debe contener al menos un número o símbolo especial',
});
/** Paginación estándar de los listados. */
export const paginacionSchema = z.object({
    pagina: z.coerce.number().int().min(1).default(1),
    porPagina: z.coerce.number().int().min(1).max(200).default(50),
    orden: z.enum(['asc', 'desc']).default('desc'),
    ordenarPor: z.string().trim().max(50).optional(),
});
/** Construye un esquema `z.enum` a partir de un array `as const` del dominio. */
export function enumDe(valores, mensaje) {
    return z.enum(valores, {
        errorMap: () => ({ message: mensaje ?? `Valor inválido. Opciones: ${valores.join(', ')}` }),
    });
}
//# sourceMappingURL=comunes.js.map